// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { defineComponent } from '@shared/ui-kit/utils/define-component';
import {
  Button,
  Divider,
  IconButton,
  List,
  ListItem,
  Typography,
  type ListItemProps,
} from '@mui/material';
import { AppIcon } from '@shared/ui-kit/components/app-icon';
import { classNames } from '@shared/ui-kit/utils/classnames';
import { AssetLogo, formatRelativeTime, i18n } from '@shared/ui-kit';
import { Translation } from '@shared/ui-kit/providers/translation-provider';
import { useDrawer } from '@shared/ui-kit/hooks/use-drawer';
import { useTranslation } from 'react-i18next';
import { type AdvisoryBank } from '@shared/data-access/types/brokerage-types';
import { cleanErrorMessage, getTranslationKey } from '../../utils/error-codes';
import { ReconnectAccountButton } from '../reconnect-account-button/reconnect-account-button';
import { ShowInAppClient } from '@shared/ui-kit/utils/app-utils';

export interface SyncAccountsDrawerProps {
  institution?: AdvisoryBank | null;
  open: boolean;
  onClose: () => void;
  onSync?: () => void;
  onRemoveBank?: (institutionId: string, institutionName: string) => void;
}

export const SyncAccountsDrawer = defineComponent({
  displayName: 'SyncAccountsDrawer',

  useSetup: (props: SyncAccountsDrawerProps) => {
    const { t } = useTranslation();
    const [DrawerRenderer, drawerControls] = useDrawer({
      open: props.open,
      onClose: props.onClose,
    });

    const errorMessage = props.institution?.errorMessage;
    const cleanedErrorMessage = cleanErrorMessage(errorMessage);
    const translationKey = getTranslationKey(errorMessage);
    const hasError = !!errorMessage;

    return {
      ...props,
      ...drawerControls,
      t,
      DrawerRenderer,
      hasError,
      cleanedErrorMessage,
      translationKey,
    };
  },

  Slots: {
    Section: ({
      divider,
      className,
      ...props
    }: ListItemProps & { divider?: boolean }) => (
      <>
        <ListItem
          className={classNames('flex flex-col !items-start p-7', className)}
          disablePadding
          disableGutters
          {...props}
        >
          {props.children}
        </ListItem>
        {divider && divider === true && <Divider />}
      </>
    ),
  },

  View: ({
    DrawerRenderer,
    onClose,
    Slots,
    institution = null,
    t,
    hasError,
    cleanedErrorMessage,
    translationKey,
    onRemoveBank,
  }) => (
    <DrawerRenderer
      anchor="right"
      PaperProps={{
        classes: {
          root: '!bg-page !w-full !max-w-2xl',
        },
      }}
    >
      <List dense disablePadding className="!flex !flex-1 !flex-col">
        <Slots.Section divider className="relative !gap-4">
          <header className="flex gap-4">
            <div className="flex size-16 items-center justify-center rounded-lg bg-section">
              {institution?.logo_url ? (
                <AssetLogo
                  logoUrl={institution?.logo_url}
                  className="size-full !object-contain"
                />
              ) : (
                <AppIcon icon="Building" color="disabled" size="lg" />
              )}
            </div>
            <div className="flex flex-col">
              <Typography
                variant="body2"
                className="!text-base !font-medium !text-title-dark"
              >
                {institution?.name}
              </Typography>

              <Typography
                variant="body2"
                className="!text-base !font-normal !text-primary-medium"
              >
                <Translation
                  i18nKey="aggregator.connected-accounts-count"
                  ns="aggregator"
                  count={institution?.products.length}
                />
              </Typography>
            </div>

            <div className="absolute right-0 top-0 ml-auto p-7">
              <IconButton
                aria-label={t('common.close', { ns: 'common' })}
                size="small"
                className="!rounded-lg"
                onClick={onClose}
                data-testid="close-button"
              >
                <AppIcon icon="Close" color="inherit" />
              </IconButton>
            </div>
          </header>

          <div
            className={classNames(
              'flex w-full items-center gap-3 rounded-lg border p-3 transition-colors',
              hasError
                ? 'border-negative/20 bg-negative/5'
                : 'border-positive/20 bg-positive/5',
            )}
          >
            <AppIcon
              icon={hasError ? 'AlertTriangle' : 'CircleCheck'}
              color={hasError ? 'danger' : 'success'}
              size="lg"
              className="flex-shrink-0"
            />
            <div className="flex flex-col">
              <Typography
                variant="body2"
                className={classNames(
                  '!text-base !font-medium',
                  hasError ? 'text-negative' : 'text-positive',
                )}
              >
                <Translation
                  i18nKey={
                    hasError
                      ? 'aggregator.connection-with-errors'
                      : 'aggregator.active-connection'
                  }
                  ns="aggregator"
                />
              </Typography>

              <Typography
                variant="caption"
                className="!text-sm !font-normal !text-primary-medium"
              >
                {institution?.last_sync ? (
                  <>
                    <Translation i18nKey="aggregator.last-sync" ns="aggregator" />
                    :&nbsp;
                    {formatRelativeTime(institution.last_sync, {
                      locale: i18n.language,
                      style: 'long',
                      direction: 'past',
                    })}
                  </>
                ) : (
                  <Translation i18nKey="aggregator.no-sync-date" ns="aggregator" />
                )}
              </Typography>

              {hasError && cleanedErrorMessage && (
                <Typography
                  variant="caption"
                  className="!text-sm !font-normal !italic !text-primary-medium"
                >
                  {translationKey
                    ? t(translationKey, { ns: 'errors' })
                    : cleanedErrorMessage}
                </Typography>
              )}
            </div>
          </div>
        </Slots.Section>

        <ShowInAppClient>
          <Slots.Section divider className="!gap-3">
            <Typography
              variant="subtitle2"
              className="!text-base !font-semibold uppercase !text-primary-medium"
            >
              <Translation
                i18nKey="aggregator.reconnect-connection-title"
                ns="aggregator"
              />
            </Typography>

            <Typography
              variant="body2"
              className="!text-base !font-normal !leading-6 !text-primary-medium"
            >
              <Translation
                i18nKey="aggregator.reconnect-connection-description"
                ns="aggregator"
              />
            </Typography>

            <ReconnectAccountButton
              institution={institution?.id}
              messageType="short"
              asButton
              fullWidth
              className="!rounded-lg !py-3"
            />
          </Slots.Section>
        </ShowInAppClient>

        <Slots.Section className="flex-grow" />
        <Slots.Section className="items-center justify-stretch gap-2">
          <Button variant="contained" color="secondary" fullWidth onClick={onClose}>
            <Translation i18nKey="common.close" ns="common" />
          </Button>

          <ShowInAppClient>
            <Button
              variant="text"
              color="error"
              fullWidth
              startIcon={<AppIcon icon="Trashcan" color="inherit" size="lg" />}
              onClick={() => {
                if (institution?.id) {
                  onRemoveBank?.(institution.id, institution.name);
                }
              }}
            >
              <Translation
                i18nKey="aggregator.disconnect-account"
                ns="aggregator"
              />
            </Button>
          </ShowInAppClient>
        </Slots.Section>
      </List>
    </DrawerRenderer>
  ),
});
